import { Component, inject, PLATFORM_ID } from '@angular/core';
import { ExportButtonComponent } from "../../../components/buttons/export-button/export-button.component";
import { ImagesService } from '../../../services/general/images.service';
import { SearchComponent } from "../../../shared/search/search.component";
import { InstitutionFilterComponent } from "../../../shared/filter/institution-filter/institution-filter.component";
import {
  NewApplicationTableComponent
} from "../../../components/card/all-product/new-application-table/new-application-table.component";
import { PaginationComponent } from "../../../components/pagination/pagination.component";
import {
  CreateNewProductComponent
} from "../../../components/card/all-product/create-new-product/create-new-product.component";
import { SuccessComponent } from '../../../components/card/success/success.component';
import { ProductService } from '../../../services/products/product.service';
import { Product, ProductQueryParams } from '../../../interfaces/responses/product-response.interfaces';
import { PaginationInterface } from '../../../interfaces/responses/pagination.interface';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ProgressLoaderComponent } from "../../../components/loaders/progress-loader/progress-loader.component";
import { PermissionService } from '../../../services/permission/permission.service';
import { ExportService } from '../../../services/general/export.service';
import { ToastService } from '../../../services/general/toast.service';

@Component({
  selector: 'app-view-all-products',
  imports: [
    ExportButtonComponent,
    InstitutionFilterComponent,
    NewApplicationTableComponent,
    PaginationComponent,
    CreateNewProductComponent,
    SuccessComponent,
    CommonModule,
    ReactiveFormsModule,
    ProgressLoaderComponent
  ],
  templateUrl: './view-all-products.component.html',
  styleUrl: './view-all-products.component.scss'
})
export class ViewAllProductsComponent {
  // Injecting required services
  _images = inject(ImagesService);
  permissionService = inject(PermissionService);
  productService = inject(ProductService);
  _fb = inject(FormBuilder);
  exportService = inject(ExportService);
  _toast = inject(ToastService);
  searchQuery!: FormGroup;
  platformId = inject(PLATFORM_ID);
  isProductLoading: boolean = false;
  viewMode: 'table' | 'grid' = 'table';

  // UI control flags
  isCreateApplication: boolean = false;
  isUpdateProduct: boolean = false;
  isViewProduct: boolean = false;
  showSuccess: boolean = false;
  product: Product | null = null;

  products: PaginationInterface<Product[]> = { data: [], meta: { page: 1, perPage: 10, total: 6, totalPages: 1 } };
  allProducts: Product[] = [];
  options = []; // Define type if applicable (e.g., string[] or SelectOption[])
  queryParams: ProductQueryParams = {
    page: 1,
    perPage: 10,
    paginate: true,
    name: '',
    categoryId: '',
  }

  /**
   * Lifecycle hook: called once the component is initialized
   */
  ngOnInit(): void {
    this.getPaginatedProducts();

    // search Query
    this.searchQuery = this._fb.group({
      name: [''],
    });

    if (isPlatformBrowser(this.platformId)) {
      this.searchQuery.get('name')?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((query: string) => {
          this.queryParams.name = query.trim();
          this.getPaginatedProducts()
        })
    }
  }


  /**
   * Fetches paginated products from the server and updates the product list
   */
  getPaginatedProducts(): void {
    this.isProductLoading = true;
    this.productService.getPaginatedProducts(this.queryParams).subscribe({
      next: (res) => {
        this.products = res;
        this.allProducts = res.data ?? [];
        this.isProductLoading = false;
      },
      error: (err) => {
        console.error('Error fetching products:', err);
        this.isProductLoading = false;
      }
    });
  }

  filterProduct(formData: FormGroup) {
    const startDate = formData.get('startDate')?.value || undefined;
    const endDate   = formData.get('endDate')?.value   || undefined;
    const status    = formData.get('status')?.value    || undefined;

    if (!startDate && !endDate && !status) {
      this.resetFilters();
      return;
    }

    this.queryParams.startDate = startDate;
    this.queryParams.endDate   = endDate;
    this.queryParams.isActive  = status === 'Active' ? true : status === 'Inactive' ? false : null;
    this.queryParams.page = 1;
    this.getPaginatedProducts();
  }

  resetFilters() {
    // Reset query params to initial state
    this.queryParams.startDate = undefined;
    this.queryParams.endDate = undefined;
    this.queryParams.isActive = null;
    this.queryParams.page = 1;
    this.getPaginatedProducts();
  }

  /**
   * Updates the current page when pagination changes
   */
  onPageChange(page: number): void {
    this.queryParams.page = page;
    this.getPaginatedProducts(); // Re-fetch data for the new page
  }

  /**
   * Toggles product view mode (e.g., for detail view or preview)
   */
  onViewNewApplication(): void {
    this.isViewProduct = !this.isViewProduct;
  }

  /**
   * Triggers the creation mode for a new application/product
   */
  onCreateApplication(): void {
    this.isCreateApplication = true;
  }

  /**
   * Closes the product/application creation form
   */
  onCloseApplication(): void {
    this.product = null;
    this.isCreateApplication = false;
  }

  onUpdateProduct(product: Product) {
    this.product = product;
  }

  onShowOverlay() {
    if (this.product) {
      this.isUpdateProduct = true;
    }
  }

  // Close handler specifically for the update overlay
  onCloseUpdateProduct() {
    this.isUpdateProduct = false;
    this.product = null;
  }

  /**
   * Handle export based on type
   */
  handleExport(type: 'Excel' | 'PDF' | 'CSV'): void {
    if (this.allProducts.length === 0) {
      this._toast.warning('No data available to export');
      return;
    }

    const exportData = this.allProducts.map(product => ({
      'Product Name': product.name || '',
      'Category': product.category?.name || '',
      'Interest Rate': product.interestRate ? `${product.interestRate}%` : '',
      'Min Amount': product.minAmount || '',
      'Max Tenure': product.maxTenure ? `${product.maxTenure} months` : '',
      'Processing Fee': product.processingFee || '',
      'Min Credit Score': product.minCreditScore || '',
      'Max Credit Score': product.maxCreditScore || '',
      'Created Date': product.createdAt ? new Date(product.createdAt).toLocaleDateString() : ''
    }));

    const filename = `products_${new Date().toISOString().split('T')[0]}`;

    switch (type) {
      case 'Excel':
        this.exportService.exportToExcel(exportData, filename);
        this._toast.success('Products exported to Excel successfully');
        break;
      case 'PDF':
        this.exportService.exportToPDF(exportData, filename, 'All Products');
        this._toast.success('Products exported to PDF successfully');
        break;
      case 'CSV':
        this.exportService.exportToCSV(exportData, filename);
        this._toast.success('Products exported to CSV successfully');
        break;
    }
  }

}
